import { KeyboardEvent, useCallback, useEffect, useState } from 'react';

type UseSearchListKeyNavArgs = {
  open: boolean;
  setOpen: (next: boolean) => void;
  /** 하이라이트 순회 대상 행 수 — 전체선택 등 부가 행 포함 */
  itemCount: number;
  /** 리스트 identity 가 바뀌면 하이라이트를 -1 로 리셋 */
  items: readonly unknown[];
  onSelectIndex: (index: number) => void;
  /** 로딩 중 true — 방향키 하이라이트 이동만 보류. Tab 은 통과(키보드 트랩 방지) */
  busy?: boolean;
};

/**
 * 인풋 포커스를 유지한 채 드롭다운 하이라이트를 키보드로 순회.
 * 옵션 행이 focusable 하지 않은 참조인용 — 행이 button 인 결재라인은 roving focus 를 쓴다.
 * Tab 은 끝단에서 닫고 기본 이동을 허용 — 키보드만으로 영역 이탈이 가능해야 한다.
 * focusedIndex 는 키보드 전용 — hover 와 결합하면 첫 Tab 이 2번째 항목으로 가는 오프바이원이 생긴다.
 */
const useSearchListKeyNav = ({
  open,
  setOpen,
  itemCount,
  items,
  onSelectIndex,
  busy = false,
}: UseSearchListKeyNavArgs) => {
  const [focusedIndex, setFocusedIndex] = useState(-1);

  useEffect(() => {
    setFocusedIndex(-1);
  }, [items]);

  useEffect(() => {
    if (!open) setFocusedIndex(-1);
  }, [open]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // 한글 조합 중 키는 무시 — 조합 글자 중복 확정 방지
      if (e.nativeEvent.isComposing) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (busy || itemCount === 0) return;
        if (!open) setOpen(true);
        setFocusedIndex((i) =>
          e.key === 'ArrowDown'
            ? i + 1 >= itemCount
              ? 0
              : i + 1
            : i - 1 < 0
            ? itemCount - 1
            : i - 1,
        );
      } else if (e.key === 'Tab') {
        if (!open || busy || itemCount === 0) return;
        if (e.shiftKey) {
          if (focusedIndex <= 0) {
            setOpen(false);
            return;
          }
          e.preventDefault();
          setFocusedIndex(focusedIndex - 1);
        } else {
          if (focusedIndex >= itemCount - 1) {
            setOpen(false);
            return;
          }
          e.preventDefault();
          setFocusedIndex(focusedIndex + 1);
        }
      } else if (e.key === 'Enter') {
        if (open && focusedIndex >= 0 && focusedIndex < itemCount) {
          e.preventDefault();
          onSelectIndex(focusedIndex);
        }
      } else if (e.key === 'Escape') {
        if (open) {
          e.preventDefault();
          setOpen(false);
        }
      }
    },
    [open, setOpen, itemCount, focusedIndex, onSelectIndex, busy],
  );

  return { focusedIndex, handleKeyDown };
};

export default useSearchListKeyNav;
